/* eslint no-console: 0 */
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import { Route, Switch, useHistory, useRouteMatch } from 'react-router-dom';
import '../../../../stylesheets/components/phone_input.scss';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle';
import { useGetCurrentUser } from '../../../hooks/useGetCurrentUser.ts';
import { useUpdateUserPhoneMutation } from '../../../hooks/useUpdateUserPhoneMutation.ts';
import { customFetchV2 } from '../../../services/custom-fetch-v2.ts';
import Button from '../../bits/Button.jsx';
import TextField from '../../bits/TextField.jsx';
import Loading from '../Loading.jsx';
import { PhoneVerificationStatusIcon } from './PhoneVerificationStatusIcon.tsx';

/** Provides consistent filled feedback for phone setup errors and success. */
const Alert: React.FC<any> = props => (
  <MuiAlert elevation={6} variant="filled" {...props} />
);

interface PhoneNumberFormProps {
  onVerificationStarted: () => void;
}

interface EditPhoneLocationState {
  origin?: string;
  settingsHistoryStepsBack?: number;
}

/**
 * Displays the saved phone number and its verification status. A changed
 * number is saved before its first verification code is sent, while the red
 * status icon lets a customer resume an incomplete verification later.
 */
const PhoneNumberForm: React.FC<PhoneNumberFormProps> = ({
  onVerificationStarted,
}) => {
  const { data: user, isLoading } = useGetCurrentUser();
  const mutation = useUpdateUserPhoneMutation();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const queryClient = useQueryClient();

  useSetModalTitle('Phone Number');

  useEffect(() => {
    setPhoneNumber(user?.phone?.number || '');
  }, [user?.phone?.number]);

  /** Saves a changed number before moving to the verification prompt. */
  const onSave = useCallback(async () => {
    setIsUpdating(true);
    setErrorMessage(null);

    try {
      await mutation.mutateAsync({ number: phoneNumber });
      await queryClient.invalidateQueries({
        queryKey: ['useGetCurrentUser'],
        refetchType: 'active',
      });
    } catch (error) {
      console.error('Failed to update phone number', error);
      setErrorMessage('Unable to save this phone number. Please try again.');
      return;
    } finally {
      setIsUpdating(false);
    }

    onVerificationStarted();
  }, [mutation, onVerificationStarted, phoneNumber, queryClient]);

  const savedNumberIsDisplayed = phoneNumber === user?.phone?.number;
  const showVerificationStatus = savedNumberIsDisplayed && !!user?.phone;

  return (
    <>
      {isLoading && <Loading />}

      {!isLoading && user && (
        <>
          <div className="phone-input-container">
            <PhoneInput
              country={user.homebaseCountry?.toLowerCase()}
              value={phoneNumber}
              onChange={editedPhoneNumber => setPhoneNumber(editedPhoneNumber)}
              enableSearch={false}
              countryCodeEditable={false}
              autoFormat={true}
              disabled={isUpdating}
              inputClass={`phoneNumberInput${showVerificationStatus ? ' phoneNumberInput--with-status' : ''}`}
              inputProps={{ autoFocus: true }}
              masks={{
                gb: '..... ......',
              }}
            />

            {showVerificationStatus && user.phone?.verified && (
              <span className="phone-verification-status-position">
                <PhoneVerificationStatusIcon verified />
              </span>
            )}

            {showVerificationStatus && !user.phone?.verified && (
              <span className="phone-verification-status-position">
                <PhoneVerificationStatusIcon
                  verified={false}
                  disabled={isUpdating}
                  onClick={onVerificationStarted}
                />
              </span>
            )}
          </div>

          <div style={{ marginTop: '30px' }}>
            <Button
              onClick={onSave}
              color="primary"
              disabled={!phoneNumber || savedNumberIsDisplayed || isUpdating}
              loading={isUpdating}
            >Update</Button>
          </div>

          {showVerificationStatus && !user.phone?.verified && (
            <div style={{ marginTop: '12px' }}>
              <Button
                onClick={onVerificationStarted}
                color="secondary"
                disabled={isUpdating}
              >Verify phone number</Button>
            </div>
          )}
        </>
      )}

      <Snackbar
        open={errorMessage !== null}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

interface VerifyPhoneProps {
  onVerified: () => void;
  verificationUrl: string;
}

/**
 * Presents the focused second step of phone setup and confirms the six-digit
 * challenge sent to the user's newly saved or previously unverified number.
 */
const VerifyPhone: React.FC<VerifyPhoneProps> = ({ onVerified, verificationUrl }) => {
  const { data: user, isLoading } = useGetCurrentUser();
  const history = useHistory<EditPhoneLocationState>();
  const [verificationCode, setVerificationCode] = useState('');
  const [
    developmentVerificationCode,
    setDevelopmentVerificationCode,
  ] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const queryClient = useQueryClient();
  const verificationRequested = history.location.pathname === `${verificationUrl}/confirm`;

  useSetModalTitle('Verify Phone Number');

  /** Sends the challenge only after the customer explicitly requests it. */
  const requestVerification = useCallback(async () => {
    setIsSendingCode(true);
    setErrorMessage(null);

    try {
      const response = await customFetchV2('/api/v2/users/me/phone/verification', {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error('Unable to send verification code');
      }

      setDevelopmentVerificationCode(response.data?.verificationCode || null);
      history.push(`${verificationUrl}/confirm`, {
        origin: 'verify_phone',
        settingsHistoryStepsBack:
          (history.location.state?.settingsHistoryStepsBack || 0) + 1,
      });
    } catch (error) {
      console.error('Failed to send phone verification code', error);
      setErrorMessage('Failed to send verification code. Please try again.');
    } finally {
      setIsSendingCode(false);
    }
  }, [history, verificationUrl]);

  /** Confirms the current challenge and refreshes the verified phone status. */
  const confirmVerification = useCallback(async () => {
    setIsVerifying(true);
    setErrorMessage(null);

    try {
      const response = await customFetchV2('/api/v2/users/me/phone/verification/confirm', {
        method: 'POST',
        body: JSON.stringify({ code: verificationCode }),
      });
      if (!response.ok) {
        throw new Error('Invalid verification code');
      }

      await queryClient.invalidateQueries({
        queryKey: ['useGetCurrentUser'],
        refetchType: 'active',
      });
    } catch (error) {
      console.error('Failed to verify phone number', error);
      setErrorMessage('The verification code is invalid or expired. Please try again.');
      return;
    } finally {
      setIsVerifying(false);
    }

    onVerified();
  }, [onVerified, queryClient, verificationCode]);

  return (
    <>
      {isLoading && <Loading />}

      {!isLoading && user?.phone && (
        <div className="phone-verification-form">
          {!verificationRequested && (
            <>
              <p className="phone-verification-instructions">
                We’ll send a 6-digit verification code to{' '}
                <strong>{user.phone.numberFormatted}</strong>.
              </p>
              <Button
                onClick={requestVerification}
                color="primary"
                disabled={isSendingCode}
                loading={isSendingCode}
              >Send code</Button>
            </>
          )}

          {verificationRequested && (
            <>
              <p className="phone-verification-instructions">
                Enter the 6-digit code we sent to <strong>{user.phone.numberFormatted}</strong>.
              </p>

              {developmentVerificationCode && (
                <Alert severity="info">
                  Development only: use code <strong>{developmentVerificationCode}</strong>.
                </Alert>
              )}

              <TextField
                autoFocus
                id="phone-verification-code"
                type="text"
                label="Verification code"
                value={verificationCode}
                fullWidth
                inputProps={{
                  autoComplete: 'one-time-code',
                  inputMode: 'numeric',
                  maxLength: 6,
                  pattern: '[0-9]*',
                }}
                disabled={isVerifying}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => (
                  setVerificationCode(event.target.value.replace(/\D/g, ''))
                )}
              />

              <div style={{ marginTop: '30px' }}>
                <Button
                  onClick={confirmVerification}
                  color="primary"
                  disabled={verificationCode.length !== 6 || isVerifying}
                  loading={isVerifying}
                >Verify phone number</Button>
              </div>
            </>
          )}
        </div>
      )}

      {!isLoading && !user?.phone && (
        <Alert severity="error">Add a phone number before requesting verification.</Alert>
      )}

      <Snackbar
        open={errorMessage !== null}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

/**
 * Coordinates the phone-number and verification screens so verification feels
 * like a pushed modal step before returning the customer to Settings.
 */
const EditPhone: React.FC = () => {
  const { path, url } = useRouteMatch();
  const history = useHistory<EditPhoneLocationState>();

  /** Pushes the confirmation step without sending a billable message. */
  const openVerification = useCallback(() => {
    history.push(`${url}/verify`, {
      origin: 'edit_phone',
      settingsHistoryStepsBack: history.location.state?.origin === 'settings_list' ? 2 : 1,
    });
  }, [history, url]);

  /** Returns to the Settings list after successful verification. */
  const finishVerification = useCallback(() => {
    const settingsUrl = url.replace(/\/edit-phone$/, '');
    const successState = {
      phoneVerificationSucceeded: true,
    };

    const { settingsHistoryStepsBack } = history.location.state || {};

    if (settingsHistoryStepsBack) {
      const stopListening = history.listen((location, action) => {
        if (action !== 'POP') return;

        stopListening();
        history.replace({
          ...location,
          pathname: settingsUrl,
          state: {
            ...location.state,
            ...successState,
          },
        });
      });

      history.go(-settingsHistoryStepsBack);
      return;
    }

    history.replace(settingsUrl, successState);
  }, [history, url]);

  return (
    <Switch>
      <Route path={`${path}/verify`}>
        <VerifyPhone
          onVerified={finishVerification}
          verificationUrl={`${url}/verify`}
        />
      </Route>
      <Route exact path={path}>
        <PhoneNumberForm onVerificationStarted={openVerification} />
      </Route>
    </Switch>
  );
};

export { EditPhone };
